import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client.config';
import { queryKeys } from '../react-query.config';

interface SetQuestionStatOverrideParams {
  questionId: string;
  fixtureId?: string;
  playerId?: string;
  teamId?: string;
  statType: string;
  value: number;
  originalValue?: number;
  reason?: string;
}

export interface SetQuestionStatOverrideResponse {
  success: boolean;
  questionId: string;
  regraded?: boolean;
  /** True when the pickem was re-scored (points + payouts recalculated). */
  scored?: boolean;
  /** Present when scoring was skipped (e.g. already scoring, or paid payouts exist). */
  scoringSkippedReason?: string;
}

export const setQuestionStatOverride = async (
  params: SetQuestionStatOverrideParams
): Promise<SetQuestionStatOverrideResponse> => {
  return (apiClient as any).post(`/api/admin/live-scoring/question/${params.questionId}/override`, {
    playerId: params.playerId,
    teamId: params.teamId,
    statType: params.statType,
    value: params.value,
    originalValue: params.originalValue,
    reason: params.reason,
  });
};

export const useSetQuestionStatOverrideMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setQuestionStatOverride,
    onSuccess: (_, variables) => {
      // Invalidate question override check
      queryClient.invalidateQueries({
        queryKey: ['live-scoring', 'question', variables.questionId, 'has-overrides'],
      });

      // Invalidate question overrides list
      queryClient.invalidateQueries({
        queryKey: ['live-scoring', 'question', variables.questionId, 'overrides'],
      });

      // Invalidate match stats to refetch with new override values
      if (variables.fixtureId) {
        queryClient.invalidateQueries({
          queryKey: ['feed', 'matches', variables.fixtureId, 'statistics'],
        });
      }

      // Setting an override re-grades the question on the backend and persists
      // the winning option's is_correct. Refresh the questions list so the
      // "HAS ANSWER" column and the "Cannot Score" gate reflect the new result.
      queryClient.invalidateQueries({
        queryKey: queryKeys.questions.all,
      });
    },
  });
};
